import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/GridLegacy';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import StyledComponent from 'styled-components';

import ApiRequest from '../../api/request';
import { AlertContext } from '../../components';
import FormLoader from '../../components/Form/FormLoader';
import CsvButton from '../../components/Table/CsvButton';
import { CookieValues } from '../../types';
import { Datetime } from '../../utils';

const Container = StyledComponent.div`

			min-height: 80%;
			overflow-y: scroll;
			padding: 15px 15px;
			@media(min-width: 600px)
			{
				min-width: 500px;
			}

			.main-btn-section, .bottom-btn-section
			{
				margin-bottom: 25px;
				margin-top: 8px;
				justify-content: space-between;
		    display: flex;
			}
			.bottom-btn-section
			{
				margin-bottom: 0px;
				margin-top: 50px;
				justify-content: space-between;
		    display: flex;
			}
      .select-container: {
        min-width: 100%;
      }
			.schema-name
			{
				width: 100%;
				margin-top: 10px;
			}
			.schema-list
			{
				margin-top: 25px;
			}
			.table-properties-btn
			{
				margin-top: 15px;
			}
			.admin-table-properties
			{
				margin-top: 15px;
				width: 100%;
			}
			.table-properties
			{
				margin-top: 15px;
				width: 100%;
				font-size: 10px;

				textarea
				{
					white-space: pre-line;
				}
			}
			.schema-btns
			{
				margin-bottom: 10px;
			}
			.schema-form
			{
				margin-top: 15px;
			}
			.model-list
			{
				margin-top: 15px;

				h1
				{
					text-align: left;
				}
				h2
				{
					text-align: left;
					color: black;
				}
			}
			.MuiChip-outlinedPrimary
			{
				width: 100%;
				margin-top: 10px;
			}

			.job-description
			{
				color: black;
				text-align: left;
				margin: auto;
			}

			.checkbox-label
			{
				color: #3f51b5;
			}

			.select-control
			{
				width: 100%

				@media(min-width: 600px)
				{
					width: 25%;
				}
			}

			nav
			{
				display: flex !important;
				padding: 16px !important;
			}
		`;

// MARK: - Data fields
const MINUTES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27,
  28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
  52, 53, 54, 55, 56, 57, 58, 59, 60,
];
const HOURS = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
];

// TODO: Make schema fields read from backend so we can centralize changes to this page
function JobManager() {
  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const { showAlert } = useContext(AlertContext);

  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobName, setJobName] = useState<string>('');
  const [repeatMinutes, setRepeatMinutes] = useState<number>(0);
  const [repeat, setRepeat] = useState<boolean>(false);
  const filterFinishedJobsRef = useRef<boolean>(false);
  const [repeatHours, setRepeatHours] = useState<number>(0);
  const [repeatSeconds, setRepeatSeconds] = useState<number>(0);
  const [usingBatchPreset, setUsingBatchPreset] = useState<boolean | null>(null);

  useEffect(() => {
    const loadDataInterval = setInterval(() => {
      loadData(false);
    }, 8000);
    loadData(false);

    return () => {
      clearInterval(loadDataInterval);
    };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  // MARK: - APIs
  const loadData = async (showSpinner: boolean) => {
    console.log('JobManager.loadData()');
    showSpinner && setIsLoading(true);

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {
          filterFinishedJobs: filterFinishedJobsRef.current,
        },
        path: 'classy/jobs',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        return;
      }

      setJobs(response.data.results);
      setUsingBatchPreset(response.data.usingBatchPreset);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addJob = async () => {
    console.log('JobManager.addJob()');
    setIsLoading(true);

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {
          jobName,
          repeat,
          repeatHours,
          repeatMinutes,
          repeatSeconds,
        },
        path: 'classy/start-job',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        console.log(response);
        return;
      }

      console.log(response.data);

      setJobs(prevJobs => {
        const newJobs = [...prevJobs];
        newJobs.unshift(response.data.results);
        return newJobs;
      });
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const clearJobs = async () => {
    console.log('JobManager.clearJobs()');
    setIsLoading(true);
    filterFinishedJobsRef.current = true;

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params: {
          filterFinishedJobs: true,
        },
        path: 'classy/clear-jobs',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        console.log(response);
        return;
      }

      console.log(response.data.results);
      setJobs(response.data.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const cancelJob = async (jobKey: string) => {
    console.log('JobManager.cancelJob()');
    setIsLoading(true);

    const params = {
      jobKey,
      filterFinishedJobs: filterFinishedJobsRef.current,
    };

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: 'classy/cancel-job',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        console.log(response);
        return;
      }

      console.log(response.data.results);
      setJobs(response.data.results);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const setUsingBatchPresetApi = async () => {
    console.log('JobManager.setUsingBatchPreset()');
    setIsLoading(true);

    const params = {
      usingBatchPreset: !usingBatchPreset,
    };

    try {
      const response = await ApiRequest.sendRequest({
        method: 'post',
        params,
        path: 'classy/use-batch-preset',
        token: cookies.token,
      });
      if (response.data.error !== null) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: response.data.error,
          variant: 'danger',
          dismissText: 'okay',
        });
        console.log(response);
        return;
      }

      setUsingBatchPreset(!usingBatchPreset);
    } catch (err: any) {
      showAlert({
        headerText: 'Uh-oh',
        detailText: `An error has occurred, please try again or contact support.\nError: ${err.message}`,
        variant: 'danger',
        dismissText: 'okay',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCsv = () => {
    const formattedData: any[] = [];

    jobs.forEach(job => {
      const formattedRow = {};
      Object.keys(job).forEach(key => {
        switch (key) {
          case 'createdBy':
            // @ts-ignore
            formattedRow[key] = job[key].username;
            break;
          case 'startedOn':
          case 'finishedOn':
            // @ts-ignore
            formattedRow[key] = Datetime.formatFullDate(new Date(job[key]));
            break;
          case '_id':
          case 'isDeleted':
          case 'createdOn':
          case 'key':
          case 'updatedOn':
          case '__v':
            break;
          default:
            // @ts-ignore
            formattedRow[key] = job[key];
        }
      });

      // @ts-ignore
      formattedRow.JobId = `${formattedRow.name}-${formattedRow.id}`;
      // @ts-ignore
      delete formattedRow.name;
      // @ts-ignore
      delete formattedRow.id;

      formattedData.push(formattedRow);
    });

    return formattedData;
  };

  return (
    <Paper>
      <Container>
        <link rel="stylesheet" href="https://www.herokucdn.com/purple3/latest/purple3.min.css" />
        <FormLoader isLoading={isLoading} />
        <Grid container spacing={1} style={{ width: '100%' }}>
          <Grid item xs={12}>
            <DialogTitle>
              <Typography
                variant="h3"
                classes={{ h3: 'friends-title' }}
                display="block"
                align="center">
                Batch Job Manager
              </Typography>
              {jobs && jobs.length > 0 && (
                <CsvButton getCsvData={exportToCsv} fileName="batch-job-report" headers={[]} />
              )}
            </DialogTitle>

            <Grid item style={{ marginTop: '25px' }}>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Calculate Total Raised - Iterate all transactions in Classy and calculate the total
                raised
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Update Answers - Update the size question answer for all members with missing
                response
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Update Teams - Fix any member records where team was not imported yet when member
                was imported. Also update total raised for team.
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                {`Registration Types - Update member's goal amount to honor registration type overrides`}
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                All - Will run each batch in order of: import members, import teams, update teams,
                registration types, calculate totals
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Send emails - Sends the QR code email TO EVERY MEMBER IN AN EVENT
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Members and teams template - Uses the newly optimized job to import members and
                teams
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Tickets and answers template - Uses the newly optimized job to import tickets and
                answers
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                calculate totals raised and update teams template - Uses the newly optimized job to
                calculate totals
              </Typography>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                Heroku instance manager - Checks if event day and scales up Heroku dynos. If day
                after event, scales down.
              </Typography>
            </Grid>

            <Grid item style={{ marginTop: '25px' }}>
              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                You cannot schedule two jobs to repeat at the same intervals.
              </Typography>

              <Typography
                gutterBottom
                variant="subtitle1"
                component="h2"
                className="job-description">
                {`Click "Use suggested batch schedule" and the system will schedule all jobs for you`}
              </Typography>

              {usingBatchPreset !== null && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={usingBatchPreset}
                      onChange={setUsingBatchPresetApi}
                      name="repeatCheckBox"
                      color="primary"
                      disabled={isLoading}
                    />
                  }
                  classes={{ label: 'checkbox-label' }}
                  label="Use suggested batch schedule"
                />
              )}
            </Grid>

            <Grid item xs={12} style={{ marginTop: '25px' }}>
              <FormControl style={{ width: '100%' }} className="select-control">
                <InputLabel id="job-select-label">Job Type</InputLabel>
                <Select
                  labelId="job-select-label"
                  id="job-select"
                  value={jobName}
                  onChange={e => setJobName(e.target.value)}>
                  <MenuItem value="calculate-totals">Calculate Total Raised</MenuItem>
                  <MenuItem value="update-answers">Update Answers</MenuItem>
                  <MenuItem value="update-teams">Update Teams</MenuItem>
                  <MenuItem value="tickets">Registration Types</MenuItem>
                  <MenuItem value="send-emails">Send emails</MenuItem>
                  <MenuItem value="event-day-members-teams">Members and teams</MenuItem>
                  <MenuItem value="event-day-tickets-answers">
                    Tickets and answers template
                  </MenuItem>
                  <MenuItem value="event-day-totals-update-teams">
                    Calculate total raised and update teams template
                  </MenuItem>
                  <MenuItem value="check-event-day">Heroku instance manager</MenuItem>
                  <MenuItem value="airtable-update">Airtable Update</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} style={{ marginTop: '25px' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={repeat}
                    onChange={e => setRepeat(e.target.checked)}
                    name="repeatCheckBox"
                    color="primary"
                  />
                }
                classes={{ label: 'checkbox-label', root: 'select-container' }}
                label="Repeat"
              />
            </Grid>

            {repeat && (
              <>
                <Grid item xs={12} style={{ marginTop: '25px' }}>
                  <FormControl className="select-control">
                    <InputLabel id="repeat-hours-label">Every (N) Hours: </InputLabel>
                    <Select
                      labelId="repeat-hours-label"
                      id="hours-select"
                      value={repeatHours}
                      onChange={e => setRepeatHours(e.target.value)}>
                      {HOURS.map((hour) => (
                        <MenuItem value={hour}>{hour}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} style={{ marginTop: '25px' }}>
                  <FormControl className="select-control">
                    <InputLabel id="repeat-minutes-label">Every (N) Minutes: </InputLabel>
                    <Select
                      labelId="repeat-minutes-label"
                      id="minutes-select"
                      value={repeatMinutes}
                      onChange={e => setRepeatMinutes(e.target.value)}>
                      {MINUTES.map((minute) => (
                        <MenuItem value={minute}>{minute}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} style={{ marginTop: '25px' }}>
                  <FormControl className="select-control">
                    <InputLabel id="repeat-seconds-label">Every (N) Seconds: </InputLabel>
                    <Select
                      labelId="repeat-seconds-label"
                      id="seconds-select"
                      value={repeatSeconds}
                      onChange={e => setRepeatSeconds(e.target.value)}>
                      {MINUTES.map((second) => (
                        <MenuItem value={second}>{second}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {usingBatchPreset === null ? (
              <p>Loading...</p>
            ) : (
              <Grid item xs={12} style={{ marginTop: '25px' }}>
                <Button color="primary" startIcon={<AddIcon />} onClick={addJob}>
                  Add
                </Button>
                {filterFinishedJobsRef.current ? (
                  <Button
                    color="primary"
                    startIcon={<VisibilityIcon />}
                    onClick={() => {
                      filterFinishedJobsRef.current = false;
                      loadData(false);
                    }}>
                    Show Inactive and Completed Jobs
                  </Button>
                ) : (
                  <Button color="primary" startIcon={<ClearIcon />} onClick={clearJobs}>
                    Hide Inactive and Completed Jobs
                  </Button>
                )}
              </Grid>
            )}
          </Grid>

          {jobs.map((job) => {
            let bgColor = 'bg-light-purple';
            let { progress } = job;
            if (job.state === 'completed') {
              bgColor = 'bg-purple';
              progress = 100;
            } else if (job.state === 'failed') {
              bgColor = 'bg-dark-red';
              progress = 100;
            }

            const startedOn = job.startedOn
              ? Datetime.formatFullDate(new Date(job.startedOn))
              : 'Not started yet';
            const finishedOn = job.finishedOn
              ? Datetime.formatFullDate(new Date(job.finishedOn))
              : 'TBD';
            const runtime = job.runtime ? `${job.runtime} seconds` : '';

            return (
              <Grid key={`job-row-${job.id.toString()}`} item xs={12} style={{ marginTop: '25px' }}>
                <div className="flex justify-between mb2">
                  <div className="mt2 mb1">
                    <span className="hk-label">Job ID:</span>
                    {`${job.name}-${job.id}`}
                  </div>
                  <div className="mt2 mb1">
                    <span className="hk-label">State:</span>
                    {job.state}
                  </div>
                </div>
                <div className="w-100 br1 shadow-inner-1 bg-light-silver">
                  <span className={`db h1 br1 ${bgColor}`} style={{ width: `${progress}%` }} />
                </div>
                <div className="flex justify-between mb2">
                  <div className="mt2 mb1">
                    <span className="hk-label">Started On:</span>
                    {`${startedOn}`}
                  </div>
                  <div className="mt2 mb1">
                    <span className="hk-label">End On:</span>
                    {`${finishedOn}`}
                  </div>
                  <div className="mt2 mb1">
                    <span className="hk-label">Runtime:</span>
                    {`${runtime}`}
                  </div>
                </div>
                <div className="flex justify-between mb2">
                  {!job.finishedOn && (
                    <Button
                      color="primary"
                      startIcon={<ClearIcon />}
                      onClick={() => cancelJob(job.key)}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Paper>
  );
}

export default JobManager;
